import { useRef, useState } from 'react'
import {
  Autocomplete,
  Button,
  Group,
  InputBase,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { IMaskInput } from 'react-imask'
import { IconBuildingHospital, IconCircleCheckFilled } from '@tabler/icons-react'
import type { Patient } from '@medplum/fhirtypes'
import { useClient } from '@/hooks/useClient'
import { states } from '@/utils/states.ts'
import { useRecordLocationSuggestions } from './useRecordLocationSuggestions'
import { useRecordsFacilities } from './useRecordsFacilities'
import { createInitialAuthorizationFormState, normalizeFacilityName } from './authorizationFormData'
import type { FacilityDetails } from './authorizationFormData'

type Props = {
  patient: Patient
  onSaved: (facility: FacilityDetails) => void
  onCancel: () => void
}

export const AddRecordsFacilityForm = ({ patient, onSaved, onCancel }: Props) => {
  const client = useClient()
  const { suggestions, loading } = useRecordLocationSuggestions(patient)
  const isSelectingRef = useRef(false)
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)

  const form = useForm<FacilityDetails>({
    initialValues: createInitialAuthorizationFormState().facility,
    validate: {
      name: (v) => {
        if (!v.trim()) return 'Facility name is required'
        return v.trim().length < 3 ? 'Facility name must be at least 3 characters' : null
      },
      street: (v) => {
        if (!v.trim()) return 'Street address is required'
        return v.trim().length < 3 ? 'Street address must be at least 3 characters' : null
      },
      city: (v) => {
        if (!v.trim()) return 'City is required'
        return v.trim().length < 2 ? 'City must be at least 2 characters' : null
      },
      // Mantine's Select sets the value to null when deselected, so guard
      // against null before calling .trim() (would throw and abort validation).
      state: (v: string | null) => (!v?.trim() ? 'State is required' : null),
      zip: (v) => {
        if (!v.trim()) return 'ZIP code is required'
        const digits = v.replace(/\D/g, '')
        return digits.length === 5 || digits.length === 9 ? null : 'ZIP code must be 5 or 9 digits'
      },
      fax: (v) => {
        const digits = v.replace(/\D/g, '')
        if (!digits) return 'Fax number is required'
        return digits.length === 10 ? null : 'Invalid fax number'
      },
      phone: (v) => {
        if (!v) return null
        return v.replace(/\D/g, '').length === 10 ? null : 'Invalid phone number'
      },
    },
  })

  // Dedupe search across ALL platform facilities (labs, radiology, records,
  // care orgs …) — not just the records-request directory.
  const { facilities: savedFacilities, loading: savedLoading } = useRecordsFacilities(
    form.values.name,
    'all',
  )

  // Normalized names of places the patient has been — drives the green
  // "been here" indicator on matching autocomplete options.
  const visitedNames = new Set(suggestions.map((s) => normalizeFacilityName(s.name)))

  // Merge platform-search results + record-location names, deduped by name
  // (platform results take priority). Platform results are already filtered
  // server-side by the typed text; record-location hints are shown in full
  // only while the field is EMPTY — once the user types, they must match the
  // text to appear, like any other suggestion.
  const typedQuery = form.values.name.trim().toLowerCase()
  const suggestionData = (() => {
    const seen = new Set<string>()
    const data: { value: string; label: string }[] = []
    savedFacilities.forEach((f) => {
      if (seen.has(f.name)) return
      seen.add(f.name)
      data.push({ value: f.name, label: f.name })
    })
    suggestions
      .filter((s) => !typedQuery || s.name.toLowerCase().includes(typedQuery))
      .forEach((s) => {
        if (seen.has(s.name)) return
        seen.add(s.name)
        data.push({ value: s.name, label: s.name })
      })
    return data
  })()

  const handleAdd = async () => {
    if (form.validate().hasErrors) return
    const v = form.values

    setSaving(true)
    try {
      // Details the CC entered — the link endpoint uses them to fill in any
      // fields the existing org is MISSING (e.g. a fax it never had), without
      // ever overwriting values the org already has.
      const details = {
        street: v.street,
        city: v.city,
        state: v.state,
        zip: v.zip,
        ...(v.fax.trim() ? { fax: v.fax } : {}),
        ...(v.phone.trim() ? { phone: v.phone } : {}),
      }

      const notifyFilled = (facilityName: string, filledFields: string[]) => {
        if (!filledFields.length) return
        notifications.show({
          title: 'Facility details updated',
          message: `Saved missing ${filledFields.join(', ')} to ${facilityName}.`,
          color: 'green',
        })
      }

      // Reuse a selected existing facility: LINK it into the records-request
      // directory (idempotent marker tag + fill-missing-details only).
      if (selectedFacilityId) {
        const linked = await client.recordsFacilityLink({
          organizationId: selectedFacilityId,
          details,
        })
        notifyFilled(v.name, linked.filledFields)
        onSaved({ ...v, organizationId: selectedFacilityId })
        return
      }

      // Authoritative dedupe at submit time: an exact-name match anywhere on
      // the platform is linked instead of duplicated (covers names typed in
      // full without picking the suggestion).
      const { facilities: matches } = await client.facilitySearch({
        query: v.name.trim(),
        scope: 'all',
      })
      const exact = matches.find((f) => f.name.trim().toLowerCase() === v.name.trim().toLowerCase())
      if (exact) {
        const linked = await client.recordsFacilityLink({ organizationId: exact.id, details })
        notifications.show({
          title: 'Facility linked',
          message: `${exact.name} already exists on the platform and was added to the facility directory.`,
          color: 'green',
        })
        notifyFilled(exact.name, linked.filledFields)
        onSaved({ ...v, organizationId: exact.id })
        return
      }

      // Genuinely new facility.
      const created = await client.recordsFacilityCreate({
        name: v.name,
        street: v.street,
        city: v.city,
        state: v.state,
        zip: v.zip,
        ...(v.fax.trim() ? { fax: v.fax } : {}),
        ...(v.phone.trim() ? { phone: v.phone } : {}),
      })
      notifications.show({
        title: 'Facility added',
        message: `${v.name} was saved to the facility directory.`,
        color: 'green',
      })
      onSaved({ ...v, organizationId: created.id })
    } catch (err) {
      logError('AuthorizationRelease', 'add records facility', err)
      notifications.show({
        title: 'Could not save facility',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap="md">
      <Text fw={600} size="lg">
        Add New Facility
      </Text>

      <Autocomplete
        label="Facility Name"
        placeholder="Search all platform facilities or enter a new one"
        required
        data={suggestionData}
        // Show all Record-Location suggestions; they are pre-sourced, not free-text filtered.
        filter={(input) => input.options}
        renderOption={({ option }) => (
          <Group gap="xs">
            <span>{option.value}</span>
            {visitedNames.has(normalizeFacilityName(option.value)) && (
              <Tooltip label="Patient has been to this facility" withArrow>
                <IconCircleCheckFilled size={14} className="text-green-600" />
              </Tooltip>
            )}
          </Group>
        )}
        leftSection={<IconBuildingHospital size={16} className="text-gray-400" />}
        rightSection={loading || savedLoading ? <Loader size={16} /> : undefined}
        value={form.values.name}
        error={form.errors.name}
        onChange={(v) => {
          if (isSelectingRef.current) {
            isSelectingRef.current = false
            return
          }
          // Typing diverges from any previously selected saved facility.
          setSelectedFacilityId(undefined)
          form.setFieldValue('name', v)
        }}
        onBlur={() => form.validateField('name')}
        onOptionSubmit={(selected) => {
          isSelectingRef.current = true
          // Prefer a saved facility: prefill ALL fields and remember its id.
          const saved = savedFacilities.find((f) => f.name === selected)
          if (saved) {
            form.setValues({
              name: saved.name,
              street: saved.street,
              city: saved.city,
              state: saved.state,
              zip: saved.zip,
              fax: saved.fax,
              phone: saved.phone,
            })
            setSelectedFacilityId(saved.id)
            return
          }
          // Fall back to a record-location match: prefill name + any known
          // address/phone (record locations now carry structured address fields).
          const match = suggestions.find((s) => s.name === selected)
          form.setFieldValue('name', selected)
          if (match?.street) form.setFieldValue('street', match.street)
          if (match?.city) form.setFieldValue('city', match.city)
          if (match?.state) form.setFieldValue('state', match.state)
          if (match?.zip) form.setFieldValue('zip', match.zip)
          if (match?.phone) form.setFieldValue('phone', match.phone)
          setSelectedFacilityId(undefined)
        }}
      />

      <Group grow>
        <TextInput
          label="Street Address"
          placeholder="301 Memorial Medical Pkwy"
          required
          {...form.getInputProps('street')}
        />
        <TextInput
          label="City"
          placeholder="Daytona Beach"
          required
          {...form.getInputProps('city')}
        />
      </Group>

      <Group grow>
        <Select
          label="State"
          placeholder="Select state"
          data={states}
          searchable
          required
          {...form.getInputProps('state')}
        />
        <InputBase
          label="ZIP Code"
          placeholder="00000"
          component={IMaskInput}
          mask={[{ mask: '00000' }, { mask: '00000-0000' }]}
          required
          {...form.getInputProps('zip')}
        />
      </Group>

      <Group grow>
        <InputBase
          label="Fax"
          placeholder="(000) 000-0000"
          required
          component={IMaskInput}
          mask="(000) 000-0000"
          value={form.values.fax}
          onAccept={(value) => form.setFieldValue('fax', value)}
          error={form.errors.fax}
        />
        <InputBase
          label="Phone (optional)"
          placeholder="(000) 000-0000"
          component={IMaskInput}
          mask="(000) 000-0000"
          value={form.values.phone}
          onAccept={(value) => form.setFieldValue('phone', value)}
          error={form.errors.phone}
        />
      </Group>

      <Group justify="space-between" mt="md">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="teal" loading={saving} onClick={handleAdd}>
          Add Facility
        </Button>
      </Group>
    </Stack>
  )
}
